import { useEffect, useRef, useState } from "react";
import "@/App.css";
import { motion, AnimatePresence, useScroll, useTransform, useInView } from "framer-motion";
import Lenis from "@studio-freight/lenis";
import { 
  Instagram, 
  Youtube, 
  Mail, 
  Phone, 
  ChevronDown, 
  Sparkles, 
  Camera, 
  Gem, 
  Heart,
  ArrowUpRight,
  Play,
  Menu,
  X,
  Facebook,
  Ghost,
  Video
} from "lucide-react";

// Image data from Komal Dubey's portfolio
const heroImages = [
  "https://customer-assets.emergentagent.com/job_style-portfolio-9/artifacts/jvf8wu2n_3.jpeg",
  "https://customer-assets.emergentagent.com/job_komal-fashion-port/artifacts/bw823j6x_WhatsApp%20Image%202026-03-14%20at%2011.03.27%20PM.jpeg",
  "https://customer-assets.emergentagent.com/job_komal-fashion-port/artifacts/ywtcfmwi_2.jpeg",
  "https://customer-assets.emergentagent.com/job_komal-fashion-port/artifacts/xy2uembn_6.jpeg",
  "https://customer-assets.emergentagent.com/job_komal-fashion-port/artifacts/397elkan_4.jpeg",
  "https://customer-assets.emergentagent.com/job_komal-fashion-port/artifacts/2l5ps5lh_8.jpeg",
];

const galleryImages = [
  "https://customer-assets.emergentagent.com/job_style-portfolio-9/artifacts/5hxnftds_4.jpeg",
  "https://customer-assets.emergentagent.com/job_style-portfolio-9/artifacts/3kyemuld_5.jpeg",
  "https://customer-assets.emergentagent.com/job_style-portfolio-9/artifacts/2z7z11uo_6.jpeg",
  "https://customer-assets.emergentagent.com/job_style-portfolio-9/artifacts/oux3oyg8_7.jpeg",
  "https://customer-assets.emergentagent.com/job_style-portfolio-9/artifacts/rw9yga17_8.jpeg",
  "https://customer-assets.emergentagent.com/job_style-portfolio-9/artifacts/e1dnuobt_9.jpeg",
  "https://customer-assets.emergentagent.com/job_style-portfolio-9/artifacts/vt7q1shc_10.jpeg",
  "https://customer-assets.emergentagent.com/job_style-portfolio-9/artifacts/tt5dc7dc_11.jpeg",
  "https://customer-assets.emergentagent.com/job_style-portfolio-9/artifacts/fmld0ce2_12.jpeg",
  "https://customer-assets.emergentagent.com/job_style-portfolio-9/artifacts/72upvkw9_3.jpeg",
  "https://customer-assets.emergentagent.com/job_style-portfolio-9/artifacts/cuyd1i6b_WhatsApp%20Image%202026-03-14%20at%2011.03.27%20PM.jpeg",
  "https://customer-assets.emergentagent.com/job_style-portfolio-9/artifacts/xisw3ji0_2.jpeg",
  "https://customer-assets.emergentagent.com/job_style-portfolio-9/artifacts/er2ski8d_3.jpeg",
  "https://customer-assets.emergentagent.com/job_style-portfolio-9/artifacts/10zju04z_4.jpeg",
  "https://customer-assets.emergentagent.com/job_style-portfolio-9/artifacts/nlalur9b_5.jpeg",
  "https://customer-assets.emergentagent.com/job_style-portfolio-9/artifacts/mzpwab5k_6.jpeg",
  "https://customer-assets.emergentagent.com/job_style-portfolio-9/artifacts/q2afxcww_7.jpeg",
  "https://customer-assets.emergentagent.com/job_style-portfolio-9/artifacts/kt3dk2or_8.jpeg",
  "https://customer-assets.emergentagent.com/job_style-portfolio-9/artifacts/kkj1hryp_9.jpeg",
  "https://customer-assets.emergentagent.com/job_style-portfolio-9/artifacts/cxnfow41_10.jpeg",
];

// Social Media Links
const socialLinks = {
  instagram: "https://www.instagram.com/veronickaversace?igsh=MXVvc20yZ29xbmZxeA%3D%3D&utm_source=qr",
  snapchat: "https://snapchat.com/t/TjWvxkOG",
  facebook: "https://www.facebook.com/share/1N4Tyc4V5F/?mibextid=wwXIfr",
};

// Instagram Reels/Videos - Using embed URLs
const videoReels = [
  {
    id: 1,
    embedUrl: "https://www.instagram.com/reel/C1234567/embed",
    thumbnail: "https://customer-assets.emergentagent.com/job_style-portfolio-9/artifacts/jvf8wu2n_3.jpeg",
    title: "Fashion Reel",
    link: "https://www.instagram.com/veronickaversace/reels/"
  },
  {
    id: 2,
    embedUrl: "https://www.instagram.com/reel/C2345678/embed",
    thumbnail: "https://customer-assets.emergentagent.com/job_komal-fashion-port/artifacts/397elkan_4.jpeg",
    title: "Style Tips",
    link: "https://www.instagram.com/veronickaversace/reels/"
  },
  {
    id: 3,
    embedUrl: "https://www.instagram.com/reel/C3456789/embed",
    thumbnail: "https://customer-assets.emergentagent.com/job_komal-fashion-port/artifacts/2l5ps5lh_8.jpeg",
    title: "Behind The Scenes",
    link: "https://www.instagram.com/veronickaversace/reels/"
  },
  {
    id: 4,
    embedUrl: "https://www.instagram.com/reel/C4567890/embed",
    thumbnail: "https://customer-assets.emergentagent.com/job_komal-fashion-port/artifacts/xy2uembn_6.jpeg",
    title: "Lifestyle Moments",
    link: "https://www.instagram.com/veronickaversace/reels/"
  },
];

const specialties = [
  { icon: Sparkles, title: "Fashion Blogger", desc: "Curating the latest fashion trends and styles" },
  { icon: Heart, title: "Beauty Blogger", desc: "Exploring beauty products and techniques" },
  { icon: Gem, title: "Luxury Blogger", desc: "Showcasing premium lifestyle experiences" },
  { icon: Camera, title: "Lifestyle Blogger", desc: "Sharing authentic life moments and inspiration" },
];

const experiences = [
  "Fashion Consultant",
  "Fashion Model",
  "Commercial Model",
  "Print Model",
  "Runway Model",
  "Glamour Model",
  "Fit Model",
  "Freelance Model",
];

// Animation variants
const fadeInUp = {
  initial: { opacity: 0, y: 60 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] }
};

const staggerContainer = {
  animate: {
    transition: {
      staggerChildren: 0.1
    }
  }
};

const letterAnimation = {
  initial: { y: 100, opacity: 0 },
  animate: { y: 0, opacity: 1 }
};

// Navigation Component
const Navigation = ({ scrolled, menuOpen, setMenuOpen }) => {
  return (
    <motion.nav
      initial={{ y: -100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, delay: 0.5 }}
      className={`fixed top-0 left-0 w-full z-50 py-4 md:py-6 px-6 md:px-12 transition-all duration-500 ${
        scrolled ? "nav-blur bg-black/50" : ""
      }`}
      data-testid="navigation"
    >
      <div className="flex justify-between items-center max-w-screen-2xl mx-auto">
        <motion.a
          href="#hero"
          className="flex items-center gap-3 group"
          whileHover={{ scale: 1.02 }}
          data-testid="nav-logo"
        >
          <div className="w-10 h-10 md:w-12 md:h-12 rounded-full overflow-hidden border-2 border-white/30 group-hover:border-white/60 transition-colors">
            <img 
              src="https://customer-assets.emergentagent.com/job_komal-fashion-port/artifacts/01t8c5xn_image.png" 
              alt="Komal Dubey"
              className="w-full h-full object-cover object-top"
            />
          </div>
          <span className="font-heading text-lg md:text-2xl font-bold tracking-tighter text-white">
            Komal Dubey
          </span>
        </motion.a>

        {/* Desktop Navigation */}
        <div className="hidden md:flex items-center gap-8">
          {["About", "Specialties", "Portfolio", "Reels", "Contact"].map((item) => (
            <motion.a
              key={item}
              href={`#${item.toLowerCase()}`}
              className="text-sm tracking-widest uppercase text-white/80 hover:text-white link-underline"
              whileHover={{ y: -2 }}
              data-testid={`nav-${item.toLowerCase()}`}
            >
              {item}
            </motion.a>
          ))}
        </div>

        {/* Mobile Menu Button */}
        <motion.button
          className="md:hidden text-white p-2"
          onClick={() => setMenuOpen(!menuOpen)}
          whileTap={{ scale: 0.9 }}
          data-testid="mobile-menu-btn"
        >
          {menuOpen ? <X size={24} /> : <Menu size={24} />}
        </motion.button>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden mt-4 glass-effect rounded-lg overflow-hidden"
            data-testid="mobile-menu"
          >
            <div className="flex flex-col p-4 gap-4">
              {["About", "Specialties", "Portfolio", "Reels", "Contact"].map((item, i) => (
                <motion.a
                  key={item}
                  href={`#${item.toLowerCase()}`}
                  initial={{ x: -20, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ delay: i * 0.1 }}
                  onClick={() => setMenuOpen(false)}
                  className="text-sm tracking-widest uppercase text-white/80 hover:text-white py-2"
                >
                  {item}
                </motion.a>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
};

// Hero Section Component
const HeroSection = ({ currentSlide, setCurrentSlide }) => {
  const { scrollYProgress } = useScroll();
  const y = useTransform(scrollYProgress, [0, 1], [0, 300]);
  const opacity = useTransform(scrollYProgress, [0, 0.3], [1, 0]);

  return (
    <section id="hero" className="relative h-screen overflow-hidden" data-testid="hero-section">
      {/* Background Slider */}
      <AnimatePresence mode="wait">
        <motion.div
          key={currentSlide}
          initial={{ opacity: 0, scale: 1.1 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
          className="absolute inset-0"
        >
          <motion.img
            src={heroImages[currentSlide]}
            alt={`Komal Dubey ${currentSlide + 1}`}
            className="w-full h-full object-cover"
            style={{ y, objectPosition: 'center 35%' }}
          />
          <div className="absolute inset-0 gradient-overlay" />
        </motion.div>
      </AnimatePresence>

      {/* Content */}
      <motion.div
        style={{ opacity }}
        className="relative z-10 h-full flex flex-col justify-end pb-16 sm:pb-20 md:pb-32 px-4 sm:px-6 md:px-12 lg:px-24"
      >
        <div className="max-w-screen-2xl mx-auto w-full">
          <motion.div
            initial="initial"
            animate="animate"
            variants={staggerContainer}
            className="overflow-hidden"
          >
            <motion.h1
              className="font-heading text-4xl sm:text-5xl md:text-7xl lg:text-8xl xl:text-9xl text-white tracking-tighter leading-none mb-4 sm:mb-6"
              variants={fadeInUp}
              data-testid="hero-title"
            >
              Komal Dubey
            </motion.h1>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8, duration: 0.8 }}
            className="flex flex-wrap gap-2 sm:gap-3 md:gap-6 mb-6 sm:mb-8"
          >
            {["Fashion", "Beauty", "Luxury", "Lifestyle"].map((tag, i) => (
              <motion.span
                key={tag}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 1 + i * 0.15 }}
                className="text-xs sm:text-sm md:text-base tracking-[0.2em] sm:tracking-[0.3em] uppercase text-white/70 font-light"
              >
                {tag}
              </motion.span>
            ))}
          </motion.div>

          <motion.a
            href="#contact"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.5 }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="inline-flex items-center gap-2 sm:gap-3 bg-white text-black px-5 sm:px-8 py-3 sm:py-4 rounded-full font-medium hover:bg-neutral-200 transition-all duration-300 tracking-wide text-xs sm:text-sm uppercase"
            data-testid="hero-cta"
          >
            Let's Connect
            <ArrowUpRight size={18} />
          </motion.a>
        </div>

        {/* Slider Dots */}
        <div className="absolute bottom-4 sm:bottom-8 left-1/2 -translate-x-1/2 flex gap-2" data-testid="slider-dots">
          {heroImages.map((_, i) => (
            <button
              key={i}
              onClick={() => setCurrentSlide(i)}
              className={`h-1 rounded-full transition-all duration-300 ${
                i === currentSlide ? "w-6 sm:w-8 bg-white" : "w-2 bg-white/40 hover:bg-white/60"
              }`}
              data-testid={`slider-dot-${i}`}
            />
          ))}
        </div>

        {/* Scroll Indicator - Hidden on mobile */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 2 }}
          className="hidden sm:flex absolute bottom-8 right-4 sm:right-6 md:right-12 flex-col items-center gap-2"
        >
          <span className="text-xs tracking-widest uppercase text-white/50 rotate-90 origin-center">Scroll</span>
          <motion.div
            animate={{ y: [0, 8, 0] }}
            transition={{ repeat: Infinity, duration: 1.5 }}
          >
            <ChevronDown size={20} className="text-white/50" />
          </motion.div>
        </motion.div>
      </motion.div>
    </section>
  );
};

// About Section Component
const AboutSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section
      id="about"
      ref={ref}
      className="py-24 md:py-32 lg:py-40 px-6 md:px-12 lg:px-24 bg-[#050505]"
      data-testid="about-section"
    >
      <div className="max-w-screen-2xl mx-auto">
        <motion.span
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-xs tracking-[0.3em] uppercase text-white/50 mb-8 block"
        >
          About Me
        </motion.span>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-24 items-center">
          {/* Text Content */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl tracking-tight mb-8 text-white" data-testid="about-title">
              Welcome to my world of <span className="italic font-normal">fashion</span> & <span className="italic font-normal">beauty</span>
            </h2>

            <p className="text-lg md:text-xl text-neutral-400 leading-relaxed mb-8 font-light">
              I'm Komal Dubey, a professional Fashion Consultant and Model based in India. With experience working with luxury brands including <span className="text-white font-medium">Louis Vuitton</span>, I bring expertise in high-end fashion styling, commercial modeling, and content creation. As a trusted fashion consultant, I help brands connect with their audience through strategic collaborations and creative campaigns.
            </p>

            <div className="grid grid-cols-2 gap-6 mb-10">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: 0.4 }}
                className="p-6 border border-white/10 bg-white/5"
                data-testid="stats-followers"
              >
                <span className="font-heading text-4xl md:text-5xl text-white">50K+</span>
                <p className="text-sm text-neutral-400 mt-2 tracking-wide uppercase">Followers</p>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: 0.5 }}
                className="p-6 border border-white/10 bg-white/5"
                data-testid="stats-collaborations"
              >
                <span className="font-heading text-4xl md:text-5xl text-white">200+</span>
                <p className="text-sm text-neutral-400 mt-2 tracking-wide uppercase">Brand Collabs</p>
              </motion.div>
            </div>

            {/* Experience Tags */}
            <div className="flex flex-wrap gap-3">
              {experiences.map((exp, i) => (
                <motion.span
                  key={exp}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={isInView ? { opacity: 1, scale: 1 } : {}}
                  transition={{ delay: 0.6 + i * 0.1 }}
                  className={`px-4 py-2 text-xs tracking-widest uppercase rounded-full ${
                    exp === "Fashion Consultant" 
                      ? "bg-white text-black font-semibold border-2 border-white" 
                      : "border border-white/20 text-white/70"
                  }`}
                >
                  {exp}
                </motion.span>
              ))}
            </div>
          </motion.div>

          {/* Image */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="relative"
          >
            <div className="aspect-[3/4] overflow-hidden image-hover-zoom">
              <img
                src="https://customer-assets.emergentagent.com/job_style-portfolio-9/artifacts/2z7z11uo_6.jpeg"
                alt="Komal Dubey"
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>
            {/* Decorative Elements */}
            <motion.div
              initial={{ scale: 0 }}
              animate={isInView ? { scale: 1 } : {}}
              transition={{ delay: 0.8, type: "spring" }}
              className="absolute -bottom-6 -left-6 w-32 h-32 border border-white/20"
            />
            <motion.div
              initial={{ scale: 0 }}
              animate={isInView ? { scale: 1 } : {}}
              transition={{ delay: 1, type: "spring" }}
              className="absolute -top-6 -right-6 w-24 h-24 bg-white/5"
            />
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Specialties Section Component
const SpecialtiesSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section
      id="specialties"
      ref={ref}
      className="py-24 md:py-32 lg:py-40 px-6 md:px-12 lg:px-24 bg-[#0A0A0A]"
      data-testid="specialties-section"
    >
      <div className="max-w-screen-2xl mx-auto">
        <motion.span
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          className="text-xs tracking-[0.3em] uppercase text-white/50 mb-4 block"
        >
          What I Do
        </motion.span>

        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.2 }}
          className="font-heading text-4xl md:text-5xl lg:text-6xl tracking-tight mb-16 text-white"
          data-testid="specialties-title"
        >
          My Specialties
        </motion.h2>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {specialties.map((specialty, i) => (
            <motion.div
              key={specialty.title}
              initial={{ opacity: 0, y: 40 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.3 + i * 0.15 }}
              className="specialty-card p-8 border border-white/10 bg-white/5 group"
              data-testid={`specialty-${i}`}
            >
              <motion.div
                whileHover={{ rotate: 15, scale: 1.1 }}
                className="w-12 h-12 flex items-center justify-center border border-white/20 rounded-full mb-6 group-hover:bg-white/10 transition-colors"
              >
                <specialty.icon size={24} className="text-white" />
              </motion.div>
              <h3 className="font-heading text-xl md:text-2xl text-white mb-3">{specialty.title}</h3>
              <p className="text-neutral-400 text-sm leading-relaxed">{specialty.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Portfolio Gallery Section Component
const PortfolioSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });
  const [selectedImageIndex, setSelectedImageIndex] = useState(null);

  const openLightbox = (index) => {
    setSelectedImageIndex(index);
  };

  const closeLightbox = () => {
    setSelectedImageIndex(null);
  };

  const goToNext = (e) => {
    e.stopPropagation();
    setSelectedImageIndex((prev) => (prev + 1) % galleryImages.length);
  };

  const goToPrev = (e) => {
    e.stopPropagation();
    setSelectedImageIndex((prev) => (prev - 1 + galleryImages.length) % galleryImages.length);
  };

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (selectedImageIndex === null) return;
      if (e.key === 'ArrowRight') goToNext(e);
      if (e.key === 'ArrowLeft') goToPrev(e);
      if (e.key === 'Escape') closeLightbox();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedImageIndex]);

  return (
    <section
      id="portfolio"
      ref={ref}
      className="py-24 md:py-32 lg:py-40 px-6 md:px-12 lg:px-24 bg-[#050505]"
      data-testid="portfolio-section"
    >
      <div className="max-w-screen-2xl mx-auto">
        <motion.span
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          className="text-xs tracking-[0.3em] uppercase text-white/50 mb-4 block"
        >
          My Work
        </motion.span>

        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.2 }}
          className="font-heading text-4xl md:text-5xl lg:text-6xl tracking-tight mb-16 text-white"
          data-testid="portfolio-title"
        >
          Portfolio
        </motion.h2>

        {/* Attractive Bento Grid Gallery */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
          {galleryImages.map((img, i) => {
            // Create varied sizes for visual interest
            const isLarge = i === 0 || i === 5 || i === 10 || i === 15;
            const isTall = i === 2 || i === 7 || i === 12 || i === 17;
            
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={isInView ? { opacity: 1, scale: 1 } : {}}
                transition={{ delay: 0.05 * (i % 8), duration: 0.5 }}
                className={`group cursor-pointer relative overflow-hidden rounded-xl
                  ${isLarge ? 'col-span-2 row-span-2' : ''}
                  ${isTall ? 'row-span-2' : ''}
                  ${!isLarge && !isTall ? 'aspect-square' : ''}
                `}
                onClick={() => openLightbox(i)}
                data-testid={`portfolio-item-${i}`}
              >
                <img
                  src={img}
                  alt={`Portfolio ${i + 1}`}
                  className={`w-full h-full object-cover transition-transform duration-700 group-hover:scale-110
                    ${isLarge || isTall ? 'aspect-auto min-h-[300px] md:min-h-[400px]' : ''}
                  `}
                  loading="lazy"
                />
                
                {/* Hover Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-all duration-500" />
                
                {/* Hover Content */}
                <div className="absolute inset-0 flex flex-col items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-500">
                  <motion.div
                    initial={{ scale: 0.5, opacity: 0 }}
                    whileHover={{ scale: 1, opacity: 1 }}
                    className="w-14 h-14 md:w-16 md:h-16 rounded-full bg-white/20 backdrop-blur-sm border border-white/40 flex items-center justify-center mb-3"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-white">
                      <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7"/>
                    </svg>
                  </motion.div>
                  <span className="text-white text-sm font-medium tracking-wider uppercase">View</span>
                </div>

                {/* Corner Accent */}
                <div className="absolute top-3 right-3 w-8 h-8 border-t-2 border-r-2 border-white/0 group-hover:border-white/60 transition-all duration-500 rounded-tr-lg" />
                <div className="absolute bottom-3 left-3 w-8 h-8 border-b-2 border-l-2 border-white/0 group-hover:border-white/60 transition-all duration-500 rounded-bl-lg" />
              </motion.div>
            );
          })}
        </div>
      </div>

      {/* Enhanced Lightbox with Navigation */}
      <AnimatePresence>
        {selectedImageIndex !== null && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/98 flex items-center justify-center"
            onClick={closeLightbox}
            data-testid="lightbox"
          >
            {/* Close Button */}
            <motion.button
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              className="absolute top-4 right-4 md:top-8 md:right-8 z-10 w-12 h-12 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 flex items-center justify-center text-white/70 hover:text-white hover:bg-white/20 transition-all"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={closeLightbox}
              data-testid="lightbox-close"
            >
              <X size={24} />
            </motion.button>

            {/* Image Counter */}
            <div className="absolute top-4 left-4 md:top-8 md:left-8 z-10 px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm border border-white/20">
              <span className="text-white text-sm font-medium">
                {selectedImageIndex + 1} / {galleryImages.length}
              </span>
            </div>

            {/* Previous Button */}
            <motion.button
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="absolute left-2 md:left-8 z-10 w-12 h-12 md:w-14 md:h-14 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 flex items-center justify-center text-white/70 hover:text-white hover:bg-white/20 transition-all"
              whileHover={{ scale: 1.1, x: -5 }}
              whileTap={{ scale: 0.9 }}
              onClick={goToPrev}
              data-testid="lightbox-prev"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="m15 18-6-6 6-6"/>
              </svg>
            </motion.button>

            {/* Next Button */}
            <motion.button
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="absolute right-2 md:right-8 z-10 w-12 h-12 md:w-14 md:h-14 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 flex items-center justify-center text-white/70 hover:text-white hover:bg-white/20 transition-all"
              whileHover={{ scale: 1.1, x: 5 }}
              whileTap={{ scale: 0.9 }}
              onClick={goToNext}
              data-testid="lightbox-next"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="m9 18 6-6-6-6"/>
              </svg>
            </motion.button>

            {/* Main Image */}
            <motion.div
              key={selectedImageIndex}
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
              className="relative max-w-[90vw] max-h-[85vh] mx-16"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={galleryImages[selectedImageIndex]}
                alt={`Portfolio ${selectedImageIndex + 1}`}
                className="max-w-full max-h-[85vh] object-contain rounded-lg shadow-2xl"
              />
            </motion.div>

            {/* Thumbnail Navigation */}
            <div className="absolute bottom-4 md:bottom-8 left-1/2 -translate-x-1/2 flex gap-2 overflow-x-auto max-w-[90vw] p-2 bg-black/50 backdrop-blur-sm rounded-full">
              {galleryImages.slice(0, 10).map((img, i) => (
                <motion.button
                  key={i}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedImageIndex(i);
                  }}
                  className={`w-10 h-10 md:w-12 md:h-12 rounded-full overflow-hidden border-2 transition-all flex-shrink-0 ${
                    selectedImageIndex === i ? 'border-white scale-110' : 'border-transparent opacity-60 hover:opacity-100'
                  }`}
                >
                  <img src={img} alt="" className="w-full h-full object-cover" />
                </motion.button>
              ))}
              {galleryImages.length > 10 && (
                <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-white/10 flex items-center justify-center text-white text-xs">
                  +{galleryImages.length - 10}
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

// Video Reels Section Component
const VideoReelsSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section
      id="reels"
      ref={ref}
      className="py-24 md:py-32 lg:py-40 px-6 md:px-12 lg:px-24 bg-[#0A0A0A]"
      data-testid="reels-section"
    >
      <div className="max-w-screen-2xl mx-auto">
        <motion.span
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          className="text-xs tracking-[0.3em] uppercase text-white/50 mb-4 block"
        >
          Watch My Content
        </motion.span>

        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16">
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.2 }}
            className="font-heading text-4xl md:text-5xl lg:text-6xl tracking-tight text-white"
            data-testid="reels-title"
          >
            Video <span className="italic font-normal">Reels</span>
          </motion.h2>

          <motion.a
            href={socialLinks.instagram}
            target="_blank"
            rel="noopener noreferrer"
            initial={{ opacity: 0, x: 20 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ delay: 0.4 }}
            whileHover={{ scale: 1.05 }}
            className="inline-flex items-center gap-3 bg-gradient-to-r from-purple-600 to-pink-500 text-white px-6 py-3 rounded-full font-medium transition-all duration-300 tracking-wide text-sm uppercase w-fit"
            data-testid="view-all-reels"
          >
            <Instagram size={18} />
            View All on Instagram
            <ArrowUpRight size={16} />
          </motion.a>
        </div>

        {/* Video Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {videoReels.map((reel, i) => (
            <motion.a
              key={reel.id}
              href={reel.link}
              target="_blank"
              rel="noopener noreferrer"
              initial={{ opacity: 0, y: 40 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.3 + i * 0.15 }}
              whileHover={{ y: -8 }}
              className="group relative aspect-[9/16] overflow-hidden rounded-2xl bg-neutral-900"
              data-testid={`reel-${i}`}
            >
              {/* Thumbnail */}
              <img
                src={reel.thumbnail}
                alt={reel.title}
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                loading="lazy"
              />
              
              {/* Overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-60 group-hover:opacity-80 transition-opacity duration-300" />
              
              {/* Play Button */}
              <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                whileHover={{ scale: 1, opacity: 1 }}
                className="absolute inset-0 flex items-center justify-center"
              >
                <div className="w-16 h-16 rounded-full bg-white/20 backdrop-blur-sm border border-white/30 flex items-center justify-center group-hover:bg-white/30 transition-all duration-300">
                  <Play size={28} className="text-white ml-1" fill="white" />
                </div>
              </motion.div>

              {/* Title */}
              <div className="absolute bottom-0 left-0 right-0 p-4">
                <p className="text-white font-medium text-lg">{reel.title}</p>
                <div className="flex items-center gap-2 mt-2 text-white/70 text-sm">
                  <Instagram size={14} />
                  <span>Watch on Instagram</span>
                </div>
              </div>

              {/* Instagram Icon Badge */}
              <div className="absolute top-4 right-4 w-10 h-10 rounded-full bg-gradient-to-br from-purple-600 to-pink-500 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <Instagram size={18} className="text-white" />
              </div>
            </motion.a>
          ))}
        </div>

        {/* Instagram CTA Banner */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.8 }}
          className="mt-16 p-8 md:p-12 rounded-3xl bg-gradient-to-r from-purple-900/30 to-pink-900/30 border border-white/10 flex flex-col md:flex-row items-center justify-between gap-6"
        >
          <div className="text-center md:text-left">
            <h3 className="font-heading text-2xl md:text-3xl text-white mb-2">Follow for Daily Content</h3>
            <p className="text-neutral-400">Fashion tips, lifestyle moments & behind the scenes</p>
          </div>
          <div className="flex gap-4">
            <motion.a
              href={socialLinks.instagram}
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="flex items-center gap-2 bg-gradient-to-r from-purple-600 to-pink-500 text-white px-6 py-3 rounded-full font-medium"
            >
              <Instagram size={20} />
              Instagram
            </motion.a>
            <motion.a
              href={socialLinks.snapchat}
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="flex items-center gap-2 bg-yellow-400 text-black px-6 py-3 rounded-full font-medium"
            >
              <Ghost size={20} />
              Snapchat
            </motion.a>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// Contact Section Component
const ContactSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section
      id="contact"
      ref={ref}
      className="py-24 md:py-32 lg:py-40 px-6 md:px-12 lg:px-24 bg-[#0A0A0A] relative overflow-hidden"
      data-testid="contact-section"
    >
      {/* Background Decoration */}
      <div className="absolute inset-0 opacity-30">
        <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-radial from-white/5 to-transparent rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-gradient-radial from-white/5 to-transparent rounded-full blur-3xl" />
      </div>

      <div className="max-w-screen-2xl mx-auto relative z-10">
        <motion.span
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          className="text-xs tracking-[0.3em] uppercase text-white/50 mb-4 block"
        >
          Get In Touch
        </motion.span>

        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.2 }}
          className="font-heading text-4xl md:text-5xl lg:text-7xl tracking-tight mb-8 text-white"
          data-testid="contact-title"
        >
          Let's Work <span className="italic font-normal">Together</span>
        </motion.h2>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.3 }}
          className="text-lg md:text-xl text-neutral-400 mb-12 max-w-2xl"
        >
          Interested in collaborating? Get in touch with me through any of the channels below.
        </motion.p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
          {/* Email */}
          <motion.a
            href="mailto:connect@komaldubey.com"
            initial={{ opacity: 0, x: -30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ delay: 0.4 }}
            whileHover={{ scale: 1.02 }}
            className="flex items-center gap-6 p-6 border border-white/10 bg-white/5 hover:bg-white/10 transition-all group"
            data-testid="contact-email"
          >
            <div className="w-14 h-14 rounded-full border border-white/20 flex items-center justify-center group-hover:bg-white/10 transition-colors">
              <Mail size={24} className="text-white" />
            </div>
            <div>
              <span className="text-xs tracking-widest uppercase text-neutral-500 block mb-1">Email</span>
              <span className="text-lg md:text-xl text-white font-light">connect@komaldubey.com</span>
            </div>
            <ArrowUpRight size={24} className="text-white/50 ml-auto group-hover:text-white transition-colors" />
          </motion.a>

          {/* Phone */}
          <motion.a
            href="tel:+919266034241"
            initial={{ opacity: 0, x: 30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ delay: 0.5 }}
            whileHover={{ scale: 1.02 }}
            className="flex items-center gap-6 p-6 border border-white/10 bg-white/5 hover:bg-white/10 transition-all group"
            data-testid="contact-phone"
          >
            <div className="w-14 h-14 rounded-full border border-white/20 flex items-center justify-center group-hover:bg-white/10 transition-colors">
              <Phone size={24} className="text-white" />
            </div>
            <div>
              <span className="text-xs tracking-widest uppercase text-neutral-500 block mb-1">Phone</span>
              <span className="text-lg md:text-xl text-white font-light">+91 92660 34241</span>
            </div>
            <ArrowUpRight size={24} className="text-white/50 ml-auto group-hover:text-white transition-colors" />
          </motion.a>
        </div>

        {/* Social Links */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.6 }}
          className="flex flex-wrap gap-4"
        >
          <span className="text-sm tracking-widest uppercase text-neutral-500 mr-4 self-center">Follow Me</span>
          {[
            { icon: Instagram, href: socialLinks.instagram, label: "Instagram", color: "hover:bg-gradient-to-br hover:from-purple-600 hover:to-pink-500" },
            { icon: Facebook, href: socialLinks.facebook, label: "Facebook", color: "hover:bg-blue-600" },
            { 
              icon: () => (
                <svg viewBox="0 0 24 24" className="w-5 h-5 text-white" fill="currentColor">
                  <path d="M12.166 5.056c.496-.087.99-.168 1.486-.245.162.494.353.98.477 1.486.087-.003.174-.008.262-.008 2.197 0 4.044 1.497 4.573 3.525.149-.032.298-.063.448-.092.498-.097.997-.184 1.496-.265-.16-.894-.456-1.747-.87-2.534-.417-.79-.96-1.51-1.608-2.133a8.907 8.907 0 00-2.218-1.565 8.945 8.945 0 00-2.585-.88 8.973 8.973 0 00-2.748-.08 8.94 8.94 0 00-2.665.681 8.908 8.908 0 00-2.329 1.392 8.87 8.87 0 00-1.766 1.971 8.835 8.835 0 00-1.097 2.406 8.86 8.86 0 00-.35 2.663c.014.9.148 1.793.4 2.654.252.86.62 1.682 1.09 2.44a8.82 8.82 0 001.632 2.028c.617.576 1.306 1.07 2.048 1.47a8.86 8.86 0 002.394.915c.843.194 1.707.283 2.57.265a8.914 8.914 0 002.548-.42 8.902 8.902 0 002.334-1.12 8.87 8.87 0 001.915-1.768 8.846 8.846 0 001.326-2.266c.158-.404.284-.82.379-1.242-.5.08-.998.167-1.496.264-.15.03-.299.062-.448.093a4.753 4.753 0 01-.573 1.326 4.805 4.805 0 01-1.006 1.098 4.827 4.827 0 01-1.302.772 4.854 4.854 0 01-1.498.382 4.874 4.874 0 01-1.547-.094 4.862 4.862 0 01-1.44-.556 4.845 4.845 0 01-1.197-.967 4.82 4.82 0 01-.866-1.295 4.8 4.8 0 01-.468-1.517 4.808 4.808 0 01-.018-1.573c.084-.527.243-1.038.473-1.516.23-.48.53-.921.89-1.31.36-.39.776-.724 1.233-.993a4.828 4.828 0 011.44-.556c.507-.12 1.03-.165 1.548-.136.517.03 1.028.134 1.515.308.156-.49.287-.987.397-1.49a6.63 6.63 0 00-1.87-.458 6.664 6.664 0 00-1.965.065z"/>
                </svg>
              ), 
              href: socialLinks.snapchat, 
              label: "Snapchat", 
              color: "hover:bg-yellow-400" 
            },
          ].map((social, i) => (
            <motion.a
              key={social.label}
              href={social.href}
              target="_blank"
              rel="noopener noreferrer"
              initial={{ opacity: 0, scale: 0 }}
              animate={isInView ? { opacity: 1, scale: 1 } : {}}
              transition={{ delay: 0.7 + i * 0.1 }}
              whileHover={{ scale: 1.1, y: -4 }}
              className={`relative w-12 h-12 rounded-full border border-white/20 flex items-center justify-center transition-all duration-300 social-icon-pulse ${social.color}`}
              data-testid={`social-${social.label.toLowerCase()}`}
            >
              {typeof social.icon === 'function' ? <social.icon /> : <social.icon size={20} className="text-white" />}
            </motion.a>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

// Floating WhatsApp Button Component
const FloatingWhatsApp = () => {
  return (
    <motion.a
      href="https://wa.me/919266034241"
      target="_blank"
      rel="noopener noreferrer"
      className="fixed bottom-6 left-6 z-50 group"
      initial={{ scale: 0, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{ delay: 1.5, type: "spring", stiffness: 200 }}
      whileHover={{ scale: 1.1 }}
      whileTap={{ scale: 0.9 }}
      data-testid="whatsapp-floating-btn"
    >
      {/* Pulse Ring Animation */}
      <motion.div
        className="absolute inset-0 rounded-full bg-green-500/30"
        animate={{
          scale: [1, 1.5, 1.5],
          opacity: [0.7, 0, 0],
        }}
        transition={{
          duration: 2,
          repeat: Infinity,
          ease: "easeOut",
        }}
      />
      <motion.div
        className="absolute inset-0 rounded-full bg-green-500/20"
        animate={{
          scale: [1, 1.8, 1.8],
          opacity: [0.5, 0, 0],
        }}
        transition={{
          duration: 2,
          repeat: Infinity,
          ease: "easeOut",
          delay: 0.3,
        }}
      />
      
      {/* Main WhatsApp Button */}
      <div className="relative w-14 h-14 md:w-16 md:h-16 rounded-full bg-green-500 shadow-lg shadow-green-500/40 group-hover:bg-green-400 transition-colors flex items-center justify-center">
        <svg viewBox="0 0 24 24" className="w-7 h-7 md:w-8 md:h-8 text-white" fill="currentColor">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
        </svg>
      </div>
      
      {/* Tooltip */}
      <motion.div
        initial={{ opacity: 0, x: -10 }}
        whileHover={{ opacity: 1, x: 0 }}
        className="absolute left-full ml-3 top-1/2 -translate-y-1/2 bg-white text-black px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap shadow-lg pointer-events-none hidden md:block"
      >
        Chat on WhatsApp
      </motion.div>
    </motion.a>
  );
};

// Footer Component
const Footer = () => {
  return (
    <footer className="py-8 px-6 md:px-12 border-t border-white/10 bg-[#050505]" data-testid="footer">
      <div className="max-w-screen-2xl mx-auto flex flex-col md:flex-row justify-between items-center gap-4">
        <span className="font-heading text-lg text-white">Komal Dubey</span>
        <span className="text-sm text-neutral-500">© 2026 All Rights Reserved</span>
      </div>
    </footer>
  );
};

// Main App Component
function App() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  // Initialize Lenis smooth scroll
  useEffect(() => {
    const lenis = new Lenis({
      duration: 1.2,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
    });

    function raf(time) {
      lenis.raf(time);
      requestAnimationFrame(raf);
    }

    requestAnimationFrame(raf);

    return () => {
      lenis.destroy();
    };
  }, []);

  // Handle scroll
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 100);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Auto-rotate hero slider
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroImages.length);
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  // Hide Emergent badge
  useEffect(() => {
    const hideBadge = () => {
      const badge = document.getElementById('emergent-badge');
      if (badge) {
        badge.style.display = 'none';
      }
    };
    hideBadge();
    // Also hide after a delay in case it loads later
    const timeout = setTimeout(hideBadge, 1000);
    return () => clearTimeout(timeout);
  }, []);

  return (
    <div className="App min-h-screen bg-[#050505]" data-testid="app-container">
      <Navigation scrolled={scrolled} menuOpen={menuOpen} setMenuOpen={setMenuOpen} />
      <HeroSection currentSlide={currentSlide} setCurrentSlide={setCurrentSlide} />
      <AboutSection />
      <SpecialtiesSection />
      <PortfolioSection />
      <VideoReelsSection />
      <ContactSection />
      <Footer />
      <FloatingWhatsApp />
    </div>
  );
}

export default App;
